import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useMotionValue } from "framer-motion";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  getBadgeQuizAction,
  restoreTechFieldAction,
} from "../../../actions/Badge/badgeActions";
import "./QuizSelection.css";
import TopicIcon from "@mui/icons-material/Topic";
import ArticleIcon from "@mui/icons-material/Article";
import QuizIcon from "@mui/icons-material/Quiz";
import AccessTimeFilledIcon from "@mui/icons-material/AccessTimeFilled";
import LogoWithWarning from "../../LogoWithWarning";
import { redirectToPageAction } from "../../../actions/userActions";
import { REDIRECT_CERTIFICATE_PAGE } from "../../../constants/Education/certificateConstants";

const QuizSelection = () => {
  const { userSelectedTechField } = useSelector(
    (state) => state?.selectedTechFieldStore,
  );
  const { quizDetails } = useSelector((state) => state.quizQuestionStore);
  const history = useHistory();
  const dispatch = useDispatch();
  const containerRef = useRef(null);
  const [showWarning, setShowWarning] = useState(false);

  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  useEffect(() => {
    dispatch(
      getBadgeQuizAction(
        userSelectedTechField?.fieldId,
        userSelectedTechField?.subFieldId,
      ),
    );
    dispatch(restoreTechFieldAction());
  }, [
    dispatch,
    userSelectedTechField?.fieldId,
    userSelectedTechField?.subFieldId,
  ]);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        mouseX.set(e.clientX - rect.left);
        mouseY.set(e.clientY - rect.top);
      }
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, [mouseX, mouseY]);

  const { userInfo } = useSelector((state) => state.userLogin);

  const handleStartQuiz = () => {
    if (userInfo) {
      history.push("/certificates/quiz-active");
    } else {
      history.push("/login");
      dispatch(
        redirectToPageAction(REDIRECT_CERTIFICATE_PAGE, {
          url: "/certificates/quiz-active",
        }),
      );
    }
  };

  const handleLogoClick = () => {
    setShowWarning(true);
  };

  const handleConfirmLeave = () => {
    setShowWarning(false);
    history.push("/");
  };

  const handleCancelLeave = () => {
    setShowWarning(false);
  };

  // Floating code snippets data
  const codeSnippets = [
    "const quiz = { start: true }",
    "function solve() { ... }",
    "while (learning) { grow(); }",
    "if (ready) { begin(); }",
    "let success = true;",
    "return certified;",
  ];

  return (
    <div className="quizSelection" ref={containerRef}>
      {/* Animated Grid Background */}
      <div className="quizSelection__gridBackground" />

      {/* Gradient Orbs */}
      <motion.div
        className="quizSelection__orb quizSelection__orb--1"
        animate={{
          x: [0, 100, 0],
          y: [0, -50, 0],
        }}
        transition={{
          duration: 20,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />
      <motion.div
        className="quizSelection__orb quizSelection__orb--2"
        animate={{
          x: [0, -80, 0],
          y: [0, 100, 0],
        }}
        transition={{
          duration: 25,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />

      {/* Floating Code Snippets */}
      {codeSnippets.map((snippet, index) => (
        <motion.div
          key={index}
          className="quizSelection__codeSnippet"
          initial={{
            opacity: 0,
            x: Math.random() * window.innerWidth,
            y: Math.random() * window.innerHeight,
          }}
          animate={{
            opacity: [0.1, 0.3, 0.1],
            y: [
              Math.random() * window.innerHeight,
              Math.random() * window.innerHeight - 200,
              Math.random() * window.innerHeight,
            ],
          }}
          transition={{
            duration: 15 + index * 2,
            repeat: Infinity,
            ease: "linear",
            delay: index * 2,
          }}
        >
          {snippet}
        </motion.div>
      ))}

      <LogoWithWarning
        brandName={["AceTechies", "Academy"]}
        showWarningModal={true}
        redirectTo="/"
        warningTitle="Are you sure you want to leave?"
        warningMessage="Your progress will not be saved if you leave this page."
      />

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="quizSelection__initialScreen"
      >
        <motion.div
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4, type: "spring", stiffness: 80 }}
          className="quizSelection__card"
        >
          <div className="quizSelection__cardContent">
            <motion.h2
              className="quizSelection__title"
              animate={{
                backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"],
              }}
              transition={{
                duration: 5,
                repeat: Infinity,
                ease: "linear",
              }}
            >
              Ready to Start ?
            </motion.h2>

            <div className="quizSelection__details">
              <motion.div
                className="quizSelection__detailRow"
                whileHover={{ scale: 1.02, x: 5 }}
                transition={{ type: "spring", stiffness: 400 }}
              >
                <span className="quizSelection__label">
                  <TopicIcon sx={{ marginRight: "10px", color: "#FFD700" }} />{" "}
                  Topic:
                </span>
                <span className="quizSelection__value">
                  {quizDetails?.quiz?.title}
                </span>
              </motion.div>

              <motion.div
                className="quizSelection__detailRow"
                whileHover={{ scale: 1.02, x: 5 }}
                transition={{ type: "spring", stiffness: 400 }}
              >
                <span className="quizSelection__label">
                  <ArticleIcon sx={{ marginRight: "10px", color: "#FFD700" }} />{" "}
                  Subtopic:
                </span>
                <span className="quizSelection__value">
                  {quizDetails?.quiz?.technologySubfieldId?.name}
                </span>
              </motion.div>

              <motion.div
                className="quizSelection__detailRow"
                whileHover={{ scale: 1.02, x: 5 }}
                transition={{ type: "spring", stiffness: 400 }}
              >
                <span className="quizSelection__label">
                  <QuizIcon sx={{ marginRight: "10px", color: "#FFD700" }} />{" "}
                  Questions:
                </span>
                <span className="quizSelection__value">
                  {quizDetails?.quiz?.totalQuestions}
                </span>
              </motion.div>

              <motion.div
                className="quizSelection__detailRow"
                whileHover={{ scale: 1.02, x: 5 }}
                transition={{ type: "spring", stiffness: 400 }}
              >
                <span className="quizSelection__label">
                  <AccessTimeFilledIcon
                    sx={{ marginRight: "10px", color: "#FFD700" }}
                  />{" "}
                  Time:
                </span>
                <span className="quizSelection__value">
                  {quizDetails?.quiz?.totalTimeSeconds / 60} minutes
                </span>
              </motion.div>
            </div>

            <motion.button
              whileTap={{ scale: 0.95 }}
              className="quizSelection__startButton"
              onClick={handleStartQuiz}
            >
              <span className="quizSelection__buttonText">Start Quiz</span>
              <motion.span className="quizSelection__buttonArrow">
                →
              </motion.span>
            </motion.button>

            {/* Stats Bar */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
              className="quizSelection__statsBar"
            >
              <div className="quizSelection__stat">
                <div className="quizSelection__statValue">
                  {quizDetails?.quiz?.totalQuestions}
                </div>
                <div className="quizSelection__statLabel">MCQs</div>
              </div>
              <div className="quizSelection__statDivider" />
              <div className="quizSelection__stat">
                <div className="quizSelection__statValue">
                  {Math.floor(quizDetails?.quiz?.totalTimeSeconds / 60)}
                </div>
                <div className="quizSelection__statLabel">Min</div>
              </div>
              <div className="quizSelection__statDivider" />
              <div className="quizSelection__stat">
                <div className="quizSelection__statValue">Beginner</div>
                <div className="quizSelection__statLabel">Level</div>
              </div>
            </motion.div>
          </div>
        </motion.div>

        {/* Warning Modal */}
        <AnimatePresence>
          {showWarning && (
            <motion.div
              className="quizSelection__modalBackdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={handleCancelLeave}
            >
              <motion.div
                className="quizSelection__warningModal"
                initial={{ opacity: 0, scale: 0.8, y: -50 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.8, y: -50 }}
                transition={{ type: "spring", stiffness: 300, damping: 25 }}
                onClick={(e) => e.stopPropagation()}
              >
                <h3 className="quizSelection__warningTitle">
                  Are you sure you want to leave?
                </h3>
                <p className="quizSelection__warningText">
                  Your progress will not be saved if you leave this page.
                </p>
                <div className="quizSelection__warningButtons">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="quizSelection__cancelButton"
                    onClick={handleCancelLeave}
                  >
                    Cancel
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="quizSelection__confirmButton"
                    onClick={handleConfirmLeave}
                  >
                    OK
                  </motion.button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

export default QuizSelection;
